import { t } from '@transifex/native';
import type { JSX } from 'react';
import { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { MAX_FILE_UPLOADS } from 'pkg/config/files';
import * as styles from 'pkg/config/styles';

import type { AttachmentCreatePayload } from 'pkg/actions/attachments';

import * as actions from 'pkg/actions';
import * as models from 'pkg/api/models';
import { AttachmentVisibility } from 'pkg/api/models/attachment';
import { useClipboardAttachments } from 'pkg/hooks/attachments';

import { useSmallScreen } from 'components/MediaQuery';

import FormAttachment from 'components/attachment/FormItem';
import File from 'components/form/File';
import { Spinner } from 'components/loaders/spinner';
import type { MaterialSymbolVariant } from 'components/material-symbols/symbols';

import Button from 'design/button';

import * as css from './styles.css';

interface UploadAttachmentStateOptions {
	onProgress?: (a: models.attachment.Attachment) => void;
	onComplete?: (a: models.attachment.Attachment) => void;
	onError?: (error: Error) => void;
}

interface UploadAttachmentState {
	upload: (payload: AttachmentCreatePayload) => void;
	deleteAttachment: (attachment: models.attachment.Attachment) => void;
	set: (attachment: models.attachment.Attachment) => void;
	reset: () => void;
	attachments: newAttachment[];
}

export function useUploadAttachmentsState({
	onProgress,
	onComplete,
	onError,
}: UploadAttachmentStateOptions): UploadAttachmentState {
	const [newAttachments, setNewAttachments] = useState<newAttachment[]>([]);

	const storeAttachmentInState = (
		attachment: models.attachment.Attachment,
		bytesLoaded: number
	) => {
		setNewAttachments((current) => {
			const index = current.findIndex((i) => i.attachment.id === attachment.id);

			if (index === -1) {
				return [
					...current,
					{
						attachment: attachment,
						bytesUploaded: bytesLoaded,
					},
				];
			}

			current[index] = {
				bytesUploaded: bytesLoaded,
				attachment: attachment,
			};

			return [...current];
		});
	};

	const handleUpload = (payload: AttachmentCreatePayload) => {
		let attachmentId: number | undefined;

		actions.attachments
			.upload(payload, {
				onProgress: (bytesLoaded, _, attachment) => {
					attachmentId = attachment.id;
					storeAttachmentInState(attachment, bytesLoaded);
					onProgress?.(attachment);
				},
			})
			.then((attachment) => {
				if (attachment === null) {
					return;
				}

				storeAttachmentInState(attachment, attachment.size);
				onComplete?.(attachment);
			})
			.catch((error) => {
				actions.flashes.show({
					title: t('Upload failed'),
					message:
						error.message ||
						t('Failed to upload attachment. Please try again.'),
				});

				setNewAttachments((current) => {
					const failedAttachment = current.find(
						(item) => item.attachment.id === attachmentId
					);

					if (failedAttachment) {
						failedAttachment.attachment.status =
							models.attachment.AttachmentStatus.Failed;
					}

					return current;
				});

				if (onError) {
					onError(error);
				}
			});
	};

	const handleDeleteNewFile = (attachment: models.attachment.Attachment) => {
		setNewAttachments(
			newAttachments.filter((a) => a.attachment.id !== attachment.id)
		);
	};

	return {
		upload: handleUpload,
		deleteAttachment: handleDeleteNewFile,
		set: (a: models.attachment.Attachment) => {
			storeAttachmentInState(a, a.size);
		},
		reset: () => {
			setNewAttachments([]);
		},
		attachments: newAttachments,
	};
}

export interface UseAttachmentsResults {
	// Is true if we're still waiting on API responses
	hasPendingAttachments: boolean;

	// True if all uploads have finished uploading
	allAttachmentsUploaded: boolean;

	setExistingAttachments: (attachments: models.attachment.Attachment[]) => void;
	readonly?: boolean;
	attachments: models.attachment.Attachment[];
	newAttachments: models.attachment.Attachment[];
	existingAttachments: models.attachment.Attachment[];

	reset: () => void;

	FileUploader: JSX.Element;
	AttachmentItems: JSX.Element;
}

interface UseAttachmentsProps {
	attachments?: models.attachment.Attachment[];
	// Limit the attachment type for the upload, e.g. AttachmentTypeImage will allow image/*
	type?: models.attachment.AttachmentType;
	readonly?: boolean;
	maxAttachments?: number;
	hideButtonLabel?: boolean;
	buttonLabel?: string;
	buttonIcon?: MaterialSymbolVariant;

	onRemoveAttachment?: (attachmentId: number) => Promise<boolean>;
	onNewAttachment?: (attachment: models.attachment.Attachment) => void;
}

interface newAttachment {
	attachment: models.attachment.Attachment;
	bytesUploaded: number;
}

export default function useAttachments({
	attachments = [],
	type,

	readonly,
	maxAttachments = MAX_FILE_UPLOADS,
	hideButtonLabel,
	buttonLabel,
	buttonIcon = 'add',

	onRemoveAttachment = null,
	onNewAttachment = null,
}: UseAttachmentsProps): UseAttachmentsResults {
	const context = useFormContext();

	const [hasPendingAttachments, setHasPendingAttachments] =
		useState<boolean>(false);
	const [prevAttachments, setPrevAttachments] = useState(attachments);

	const ref = useRef(null);

	const {
		upload,
		attachments: newAttachments,
		deleteAttachment,
		reset,
	} = useUploadAttachmentsState({
		onProgress: (a: models.attachment.Attachment) => {
			setHasPendingAttachments(false);

			if (onNewAttachment instanceof Function) {
				onNewAttachment(a);
			}
		},
		onComplete: onNewAttachment,
		onError: () => {
			setHasPendingAttachments(false);
		},
	});

	const handleNew = async (files: File[]) => {
		setHasPendingAttachments(true);

		for (let i = 0; i < files.length; i++) {
			upload({
				visibility: AttachmentVisibility.Hidden,
				file: files[i],
			});
		}
	};

	const onFilesPicked = (fileList: File[]) => {
		const files: File[] = [];
		for (let i = 0; i < fileList.length; i++) {
			files.push(fileList[i]);
		}
		handleNew(files);
	};

	useClipboardAttachments(async (files) => {
		handleNew(files);
	});

	const handleDeleteNewFile = (attachment: models.attachment.Attachment) => {
		deleteAttachment(attachment);
		onRemoveAttachment?.(attachment.id);
	};

	const handleDeletePrevFile = async (attachmentId: number) => {
		if (context) context.reset();

		if (onRemoveAttachment) {
			const attachmentHasBeenRemoved = await onRemoveAttachment(attachmentId);

			if (attachmentHasBeenRemoved) {
				setPrevAttachments(
					prevAttachments.filter((attachment) => attachment.id !== attachmentId)
				);
			}
		} else {
			setPrevAttachments(
				prevAttachments.filter((attachment) => attachment.id !== attachmentId)
			);
		}
	};

	const handleAttachmentClick = () => ref.current.click();

	const handleReset = () => {
		setPrevAttachments([]);
		reset();
	};

	let uploadLabel = t('Add attachments');
	let fileType = '*';

	switch (type) {
		case models.attachment.AttachmentType.Image:
			uploadLabel = maxAttachments === 1 ? t('Add image') : t('Add images');
			fileType = 'image/*';
			break;
		case models.attachment.AttachmentType.Video:
			uploadLabel = t('Add videos');
			fileType = 'video/*';
			break;
		case models.attachment.AttachmentType.Audio:
			uploadLabel = t('Add audio');
			fileType = 'audio/*';
			break;
	}

	if (buttonLabel?.length > 0) {
		uploadLabel = buttonLabel;
	}

	if (hideButtonLabel) {
		uploadLabel = null;
	}

	const isSmallScreen = useSmallScreen();

	const allAttachments = [
		...newAttachments.map((a) => a.attachment),
		...prevAttachments,
	];

	let FileUploader = (
		<File inputRef={ref} accept={fileType} multiple onChange={onFilesPicked}>
			<Button
				secondary
				icon={!hasPendingAttachments ? buttonIcon : null}
				onClick={handleAttachmentClick}
				testid="attachment.upload.button"
				block={isSmallScreen}
				disabled={hasPendingAttachments}>
				{hasPendingAttachments ? (
					<Spinner size="20px" color={styles.palette.blue[500]} />
				) : (
					uploadLabel
				)}
			</Button>
		</File>
	);

	if (allAttachments.length >= maxAttachments) {
		FileUploader = null;
	}

	const hasNonNormalAttachments = allAttachments.some(
		(a) => a.status !== models.attachment.AttachmentStatus.Normal
	);

	const allAttachmentsUploaded =
		!hasPendingAttachments && !hasNonNormalAttachments;

	const hasAttachments =
		newAttachments.length > 0 || prevAttachments.length > 0;

	const AttachmentItems = hasAttachments ? (
		<div className={css.wrapper}>
			{newAttachments.map((a) => (
				<FormAttachment
					key={a.attachment.id}
					attachment={a.attachment}
					bytesUploaded={a.bytesUploaded}
					onDelete={handleDeleteNewFile}
				/>
			))}
			{prevAttachments?.map((attachment) => (
				<FormAttachment
					key={attachment.id}
					attachment={attachment}
					onDelete={() => handleDeletePrevFile(attachment.id)}
					readonly={readonly}
				/>
			))}
		</div>
	) : null;

	return {
		hasPendingAttachments,
		allAttachmentsUploaded,

		// Enable manually updating prev attachments after hook is invoked
		setExistingAttachments: setPrevAttachments,

		attachments: allAttachments,
		newAttachments: newAttachments.map(({ attachment }) => attachment),
		existingAttachments: prevAttachments,

		reset: handleReset,

		FileUploader,
		AttachmentItems,
	};
}
